import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Consultation, GetConsultationListRequestPayload, RejectedFile} from '@/interface';
import {
  TabButtons,
  NoConsultations,
  ConsultationCard,
  ConsultationDetailsModal,
  UploadDocumentModal,
  CancelConfirmationModal,
  FeedbackAndRatingModal,
  ReportViewerModal,
  ReferralModal,
  FollowupModal,
  ConsultationFilters,
  ChatPanel,
} from '@/components';
import {useSelector, useDispatch} from 'react-redux';
import {
  consultationList,
  consultationNextPage,
  patientDashboardLoadingStatus,
  patientDashboardSuccessState,
  dashboardSearchTerm,
  patientDashboardFailureState,
  patientDashboardExpectedCategory,
  appSpecializations,
  patientConsultationsRejectedFiles,
  accountDeletionStatusSelector,
} from '@/services/redux/selectors';
import {
  getConsultationListRequest,
  uploadAdditionalConsultationFilesRequest,
  cancelConsultationRequest,
  setDashboardSearchTerm,
  resetPatientDashboardMessage,
  removeRejectedFiles,
  setCurrentConsultationTab,
} from '@/services/redux/slices';
import {
  ConsultationGroup,
  ConsultationAction,
  ConsultationStatus,
  ConsultationType,
  SortOption,
  PatientAccountTicketRequestStatus,
  ReferralType,
  debounceTimer,
} from '@/constants';
import {ErrorCodes, getErrorMessage, SuccessCodes} from '@/utils/getMessages';
import {useInfiniteScroll, useToast, useClickOutside, useWindowDimensions} from '@/hooks';
import {useLocation, useNavigate} from 'react-router-dom';
import {Routes} from '@/navigation/Routes';
import {positionChatPanel, resetMessage} from '@/utils/common_functions';
import {LoaderCircle} from 'lucide-react';
import {Icon, Text} from '@/ui-kits';
import {DuplicateFilesModal} from '@/components/dashboard-screen/PatientDashboard/DuplicateFilesModal';
import {InvalidFilesModal} from '@/components/dashboard-screen/PatientDashboard/InvalidFilesModal';
import {screenSize} from '@/theme';

interface FilterType {
  type: ConsultationType | null;
  status: ConsultationStatus | null;
  sort: SortOption | null;
  deadline: Date | null;
}

export const PatientConsulation: React.FC = () => {
  // ==========================
  // hooks
  // ==========================
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {showToast} = useToast();
  const chatPanelRef = useClickOutside<HTMLDivElement>(() => closeActionModal(ConsultationAction.Chat));
  const {width} = useWindowDimensions();
  const location = useLocation();

  // ==========================
  // selectors
  // ==========================
  const consultations = useSelector(consultationList);
  const patientDashboardSuccess = useSelector(patientDashboardSuccessState) as SuccessCodes;
  const patientDashboardFailure = useSelector(patientDashboardFailureState) as ErrorCodes;
  const searchTerm = useSelector(dashboardSearchTerm);
  const expected_category = useSelector(patientDashboardExpectedCategory);
  const specializations = useSelector(appSpecializations);
  const isLoading = useSelector(patientDashboardLoadingStatus);
  const nextPage = useSelector(consultationNextPage);
  const rejectedFilesFromRedux = useSelector(patientConsultationsRejectedFiles);
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const searchTermFromRedux = useSelector(dashboardSearchTerm);

  // ==========================
  // state and refs
  // ==========================

  // state management for modal and upload modal states
  const [actionModalOpen, setActionModalOpen] = useState<false | ConsultationAction>(false);
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter states
  const [filterType, setFilterType] = useState<FilterType['type']>(null);
  const [filterStatus, setFilterStatus] = useState<FilterType['status']>(null);
  const [sortBy, setSortBy] = useState<FilterType['sort']>(null);

  // Track if history tab has any data (for showing filters)
  const [hasHistoryData, setHasHistoryData] = useState<boolean>(false);

  // Duplicate file modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
  const [duplicateFileNames, setDuplicateFileNames] = useState<string[]>([]);
  const [uploadedFilesForDuplicateCheck, setUploadedFilesForDuplicateCheck] = useState<Array<{name: string; size: number}>>([]);
  const [uploadConsultationId, setUploadConsultationId] = useState<number | null>(null);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTermFromRedux);

  // Invalid file modal state
  const [showInvalidModal, setShowInvalidModal] = useState<boolean>(false);
  const [invalidFileNames, setInvalidFileNames] = useState<string[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<RejectedFile[]>([]);

  const [activeTab, setActiveTab] = useState<ConsultationGroup>(ConsultationGroup.Ongoing);

  // ==========================
  // functions
  // ==========================
  const expected_category_name = useMemo(() => {
    return specializations.find(spec => spec.id === expected_category)?.label ?? '';
  }, [expected_category]);

  const observerRef = useInfiniteScroll({
    onLoadMore: () => nextPage && setCurrentPage(nextPage),
    hasMore: nextPage !== null,
    isLoading: isLoading,
  });

  const changeActiveTab = (tab: ConsultationGroup) => {
    // reset the search term when tab is changed
    dispatch(setDashboardSearchTerm(''));
    setActiveTab(tab);
    setActionModalOpen(false);
  };

  function goToLinkedConsultation(linkedTo: Consultation['linked_to']) {
    if (!linkedTo) return;

    let linked_case_group = ConsultationGroup.Ongoing;
    if ([ConsultationStatus.AwaitingDoctorReview, ConsultationStatus.UnderReviewByDoctor].includes(linkedTo.status)) {
      linked_case_group = ConsultationGroup.Ongoing;
    }
    if ([ConsultationStatus.ReportReady].includes(linkedTo.status)) {
      linked_case_group = ConsultationGroup.Past;
    }
    if ([ConsultationStatus.Completed, ConsultationStatus.CancelledByPatient].includes(linkedTo.status)) {
      linked_case_group = ConsultationGroup.ConsultationHistory;
    }
    setActiveTab(linked_case_group);

    dispatch(setDashboardSearchTerm(linkedTo.id.toString()));
  }

  const handleInteract = (consultation: Consultation, action: ConsultationAction, e?: React.MouseEvent<HTMLButtonElement>) => {
    if (action === ConsultationAction.LinkedTo && consultation.linked_to) {
      goToLinkedConsultation(consultation.linked_to);
    }

    setSelectedConsultation(consultation);
    setActionModalOpen(action);

    if (action === ConsultationAction.Chat) {
      // capture the position of the button
      const buttonRect = e?.currentTarget?.getBoundingClientRect();
      // only set the top position of the chat panel

      // keep the chat panel at the bottom of the page
      positionChatPanel({buttonRect, chatPanelRef});
    }
  };

  function closeActionModal(action: ConsultationAction) {
    // reject is close is triggred by diff modal clean up
    if (actionModalOpen !== action) return;
    setActionModalOpen(false);
    setSelectedConsultation(null);
  }

  const handleConfirmCancel = () => {
    if (!selectedConsultation) return;
    dispatch(cancelConsultationRequest({id: selectedConsultation.id}));
  };

  const handleFilesUploaded = (files: File[]) => {
    if (!selectedConsultation) return;

    // Store file metadata and consultation ID for duplicate detection if error occurs
    setUploadedFilesForDuplicateCheck(files.map(f => ({name: f.name, size: f.size})));
    setUploadConsultationId(selectedConsultation.id);

    const payload = new FormData();
    for (const file of files) {
      payload.append('files', file);
    }
    payload.append('id', selectedConsultation.id.toString());
    dispatch(uploadAdditionalConsultationFilesRequest(payload));
  };

  const handleApplyFilters = (args: FilterType) => {
    setFilterType(args.type);
    setFilterStatus(args.status);
    setSortBy(args.sort);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilterType(null);
    setFilterStatus(null);
    setSortBy(null);
    setCurrentPage(1);
  };

  const fetchConsultations = useCallback(
    (currentPage: number) => {
      const payload: GetConsultationListRequestPayload = {
        page: currentPage,
        group: activeTab,
      };

      if (searchTerm) {
        payload.search_text = searchTerm;
      }

      // Add filters only for history tab
      if (activeTab === ConsultationGroup.ConsultationHistory) {
        if (filterType) {
          payload.type = filterType;
        }
        if (filterStatus) {
          payload.status = filterStatus;
        }
        if (sortBy) {
          payload.sort = sortBy === SortOption.DESC ? 'desc' : 'asc';
        }
      }
      dispatch(getConsultationListRequest(payload));
    },
    [activeTab, searchTerm, filterType, filterStatus, sortBy],
  );

  // ==========================
  // effects
  // ==========================
  useEffect(() => {
    if (
      patientDashboardSuccess ||
      // upload document success
      (patientDashboardSuccess === 'S-10035' && actionModalOpen === ConsultationAction.Upload) ||
      // cancel consultation success
      (patientDashboardSuccess === 'S-10037' && actionModalOpen === ConsultationAction.Cancel) ||
      // follow up consultation success
      (patientDashboardSuccess === 'S-10043' && actionModalOpen === ConsultationAction.FollowUp) ||
      // submit feedback and rating success
      (patientDashboardSuccess === 'S-10040' && actionModalOpen === ConsultationAction.Feedback)
    ) {
      // refresh consultation list when cancel and navigate to consultation history
      if (patientDashboardSuccess === 'S-10037') {
        setCurrentPage(1);
        // Navigate to consultation history tab after cancelling
        if (activeTab === ConsultationGroup.Ongoing) {
          setActiveTab(ConsultationGroup.ConsultationHistory);
        }
      }
      // navigate to the dashboard during follow up or referral
      if (patientDashboardSuccess === 'S-10043') {
        navigate(Routes.DASHBOARD);
      }

      // close action modal for what ever is open
      if (actionModalOpen) {
        closeActionModal(actionModalOpen);
      }
    }
    // display error message as toast (but exclude errors handled by modals)
    if (patientDashboardFailure) {
      if (patientDashboardFailure === 'E-10097') {
        showToast(getErrorMessage(patientDashboardFailure, {expected_category: expected_category_name}), 'failure');
      } else if (
        !['E-10013', 'E-10016', 'E-10023', 'E-10103', 'E-10081', 'E-10100', 'E-10076'].includes(patientDashboardFailure)
      ) {
        // E-10081, E-10100 (duplicate files) and invalid file errors are handled by modals, don't show toast
        showToast(getErrorMessage(patientDashboardFailure), 'failure');
      }
    }

    return () => resetMessage(() => dispatch(resetPatientDashboardMessage()));
  }, [patientDashboardSuccess, patientDashboardFailure, expected_category_name]);

  // fetch consultations when current page changes
  useEffect(() => {
    fetchConsultations(currentPage);
  }, [fetchConsultations, currentPage]);

  // reset filters when active tab changes
  useEffect(() => {
    setCurrentPage(1);
    handleResetFilters();
  }, [activeTab]);

  useEffect(() => {
    setLocalSearchTerm(searchTermFromRedux);
  }, [searchTermFromRedux]);

  // Debounced search handler
  useEffect(() => {
    const debounce = setTimeout(() => {
      if (localSearchTerm !== searchTermFromRedux) {
        dispatch(setDashboardSearchTerm(localSearchTerm));
      }
    }, debounceTimer); // 300ms debounce

    return () => {
      clearTimeout(debounce);
    };
  }, [localSearchTerm, dispatch, searchTermFromRedux]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchTerm(e.target.value);
  };

  // Track if history has data when consultations change
  useEffect(() => {
    if (activeTab === ConsultationGroup.ConsultationHistory && !isLoading) {
      if (consultations.length > 0) {
        setHasHistoryData(true);
      } else if (!hasActiveFilters && currentPage === 1) {
        setHasHistoryData(false);
      }
    }
  }, [consultations, activeTab, isLoading, currentPage]);

  // E-10081 or E-10100: Show duplicate files modal when API fails with duplicate files error
  // Only show if we don't have rejected files (invalid files take priority)
  useEffect(() => {
    if (
      (patientDashboardFailure === 'E-10081' || patientDashboardFailure === 'E-10100') &&
      uploadedFilesForDuplicateCheck.length > 0 &&
      uploadConsultationId &&
      (!rejectedFilesFromRedux || rejectedFilesFromRedux.length === 0)
    ) {
      // Find the consultation from the list
      const consultation = consultations.find(c => c.id === uploadConsultationId);

      // Detect duplicate file names within the uploaded batch
      const fileNameCounts = new Map<string, number>();
      for (const f of uploadedFilesForDuplicateCheck) {
        const fileName = f.name.toLowerCase();
        fileNameCounts.set(fileName, (fileNameCounts.get(fileName) || 0) + 1);
      }

      // Get file names that appear more than once (duplicates within batch)
      const duplicateNames: string[] = [];
      for (const f of uploadedFilesForDuplicateCheck) {
        const fileName = f.name.toLowerCase();
        if ((fileNameCounts.get(fileName) || 0) > 1) {
          // Only add if not already in the list (preserve original case)
          if (!duplicateNames.some(name => name.toLowerCase() === fileName)) {
            duplicateNames.push(f.name);
          }
        }
      }

      // Also check against existing files in the consultation (files already uploaded)
      if (consultation) {
        const existingFileNames = new Set((consultation.additional_files || []).map(f => f.name.toLowerCase()));

        for (const f of uploadedFilesForDuplicateCheck) {
          const fileName = f.name.toLowerCase();
          if (existingFileNames.has(fileName) && !duplicateNames.some(name => name.toLowerCase() === fileName)) {
            duplicateNames.push(f.name);
          }
        }
      }

      // Show modal if we found duplicates, OR if we have files and got a duplicate error (show all uploaded files as potentially problematic)
      if (duplicateNames.length > 0) {
        setDuplicateFileNames(duplicateNames);
        setShowDuplicateModal(true);
        // Reset the error to prevent toast from showing
        dispatch(resetPatientDashboardMessage());
      } else if (uploadedFilesForDuplicateCheck.length > 0) {
        // If no duplicates detected but error says duplicate, show all uploaded files
        // This handles cases where backend detects duplicates we might have missed
        setDuplicateFileNames(uploadedFilesForDuplicateCheck.map(f => f.name));
        setShowDuplicateModal(true);
        dispatch(resetPatientDashboardMessage());
      }
    }
  }, [patientDashboardFailure, uploadedFilesForDuplicateCheck, uploadConsultationId, consultations, rejectedFilesFromRedux, dispatch]);

  useEffect(() => {
    // Only process if rejectedFilesFromRedux exists and has items
    if (rejectedFilesFromRedux && rejectedFilesFromRedux.length > 0) {
      const duplicateRejected = rejectedFilesFromRedux.filter(
        rf => rf.status_code === 'E-10081' || rf.status_code === 'E-10100' || /duplicate/i.test(rf.message || ''),
      );
      const invalidRejected = rejectedFilesFromRedux.filter(
        rf => !(rf.status_code === 'E-10081' || rf.status_code === 'E-10100' || /duplicate/i.test(rf.message || '')),
      );

      if (duplicateRejected.length > 0) {
        const names = Array.from(new Set(duplicateRejected.map(r => r.filename)));
        setDuplicateFileNames(names);
        setShowDuplicateModal(true);
      }

      if (invalidRejected.length > 0) {
        setRejectedFiles(invalidRejected);
        setInvalidFileNames(invalidRejected.map(f => f.filename));
        setShowInvalidModal(true);
      } else {
        setShowInvalidModal(false);
      }
    }
  }, [rejectedFilesFromRedux]);

  // Also check for invalid file errors based on error codes
  // Exclude duplicate file error codes (E-10081, E-10100) - those are handled by duplicate modal
  useEffect(() => {
    if (patientDashboardFailure && uploadedFilesForDuplicateCheck.length > 0 && (!rejectedFilesFromRedux || rejectedFilesFromRedux.length === 0)) {
      // Don't show invalid modal if it's a duplicate file error (duplicate modal takes priority)
      const duplicateErrorCodes = ['E-10081', 'E-10100'];
      if (duplicateErrorCodes.includes(patientDashboardFailure)) {
        return; // Let duplicate modal handle this
      }

      const invalidFileErrorCodes = ['E-10013', 'E-10016', 'E-10023', 'E-10103', 'E-10068'];
      if (invalidFileErrorCodes.includes(patientDashboardFailure)) {
        const invalidNames = uploadedFilesForDuplicateCheck.map(f => f.name);
        setInvalidFileNames(invalidNames);
        setShowInvalidModal(true);
      }
    }
  }, [patientDashboardFailure, uploadedFilesForDuplicateCheck, rejectedFilesFromRedux, dispatch]);

  // Handle successful upload - clear duplicate modal state if no rejected files
  useEffect(() => {
    if (patientDashboardSuccess === 'S-10035') {
      // Clear duplicate modal state only if no rejected files (rejected files modal will be handled by the useEffect above)
      if (!rejectedFilesFromRedux || rejectedFilesFromRedux.length === 0) {
        setShowDuplicateModal(false);
        setDuplicateFileNames([]);
        setUploadConsultationId(null);
      }

      // Clear uploaded files state
      setUploadedFilesForDuplicateCheck([]);
    }
  }, [patientDashboardSuccess, rejectedFilesFromRedux]);

  useEffect(() => {
    const tab = (location.state as any)?.tab as ConsultationGroup | undefined;
    if (tab) setActiveTab(tab);
    // optional: clear state so refresh/back doesn't reapply
    window.history.replaceState({}, document.title);
  }, []);
  const hasActiveFilters = filterType !== null || filterStatus !== null || sortBy !== null || searchTerm !== '';

  // set the current consultation tab in redux so that in redux we can do some specific actions based on the tab
  useEffect(() => {
    dispatch(setCurrentConsultationTab(activeTab));
  }, [activeTab]);

  return (
    <div className="flex flex-col bg-gray-50">
      {/* Tabs */}

      <div className="flex lg:hidden mx-1 sm:mx-4 md:mx-6 gap-2 bg-white items-center min-w-0 p-2 rounded-lg border border-ui_input_border_dark/40">
        <Icon type="search_blue" className="size-4 pointer-events-none" />
        <input
          type="text"
          placeholder={'Search by Specialist Name or Case ID'}
          value={localSearchTerm}
          onChange={handleSearchChange}
          className="w-[280px] lg:w-[320px] xl:w-[380px] max-w-full outline-none text-sm font-FigtreeRegular placeholder:font-FigtreeRegular"
        />
      </div>
      <TabButtons activeTab={activeTab} onTabChange={changeActiveTab} />

      {/* // do not put condition for page == 1 else it will prevent filter from getting rendered when new page is fetched after scroll */}
      {activeTab === ConsultationGroup.ConsultationHistory && hasHistoryData && !isLoading && (
        <ConsultationFilters
          activeTab={activeTab}
          type={filterType}
          status={filterStatus}
          sort={sortBy}
          deadline={null}
          onReset={handleResetFilters}
          onApply={handleApplyFilters}
          screen_type="patient"
        />
      )}

      {/* Consultation Cards */}
      {isLoading && currentPage === 1 ? (
        <div className="flex items-center justify-center px-4 sm:px-6 py-4 sm:py-6">
          <span className="text-ui_grey">Loading...</span>
          <LoaderCircle className="size-4 animate-spin text-ui_grey" />
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6 px-4 sm:px-6 pb-4 sm:pb-6 grow">
          {consultations.length === 0 ? (
            searchTerm !== '' || (hasActiveFilters && activeTab === ConsultationGroup.ConsultationHistory) ? (
              <div className="flex items-center justify-center w-full min-h-96 px-4 sm:px-6">
                <Text variant="heading" className="!font-FigtreeRegular text-ui_blue text-center">
                  {getErrorMessage('E-10091')}
                </Text>
              </div>
            ) : (
              <NoConsultations tab={activeTab} />
            )
          ) : (
            consultations.map(consultation => (
              <ConsultationCard
                key={consultation.id}
                consultation={consultation}
                onInteract={handleInteract}
                showFavouriteIcon={activeTab === ConsultationGroup.Past || activeTab === ConsultationGroup.ConsultationHistory}
              />
            ))
          )}
          <div ref={observerRef} />
        </div>
      )}

      {/* Modal */}
      {actionModalOpen === ConsultationAction.View && selectedConsultation && (
        <ConsultationDetailsModal
          isOpen={actionModalOpen === ConsultationAction.View}
          onClose={() => closeActionModal(ConsultationAction.View)}
          doctor={selectedConsultation?.specialist}
          hospital_poc={selectedConsultation?.hospital_poc}
          uploadedFiles={selectedConsultation.files}
          preUploadedFiles={selectedConsultation.additional_files}
        />
      )}

      {/* Upload Document Modal */}
      {accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && actionModalOpen === ConsultationAction.Upload && selectedConsultation && (
        <UploadDocumentModal
          isOpen={actionModalOpen === ConsultationAction.Upload}
          onClose={() => closeActionModal(ConsultationAction.Upload)}
          onFilesUploaded={handleFilesUploaded}
        />
      )}

      {accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && actionModalOpen === ConsultationAction.Cancel && selectedConsultation && (
        <CancelConfirmationModal
          isOpen={actionModalOpen === ConsultationAction.Cancel}
          onClose={() => closeActionModal(ConsultationAction.Cancel)}
          onConfirm={handleConfirmCancel}
        />
      )}
      {actionModalOpen === ConsultationAction.Feedback && selectedConsultation && (
        <FeedbackAndRatingModal
          isOpen={actionModalOpen === ConsultationAction.Feedback}
          onClose={() => closeActionModal(ConsultationAction.Feedback)}
          consultationId={selectedConsultation.id}
          doctorName={selectedConsultation?.specialist?.name || ''}
          doctorPhoto={selectedConsultation?.specialist?.image || ''}
          doctorSpecialization={selectedConsultation?.specialist?.specialization.label || ''}
        />
      )}

      {actionModalOpen === ConsultationAction.Report && selectedConsultation && (
        <ReportViewerModal
          case_id={selectedConsultation.id}
          report_url={selectedConsultation.report || ''}
          isOpen={actionModalOpen === ConsultationAction.Report}
          onClose={() => closeActionModal(ConsultationAction.Report)}
          showDownload
        />
      )}

      {actionModalOpen === ConsultationAction.Referral && selectedConsultation?.referral && (
        <ReferralModal
          readonly={selectedConsultation.status.id === ConsultationStatus.Completed}
          case_id={selectedConsultation.id}
          referral={selectedConsultation.referral}
          isOpen={actionModalOpen === ConsultationAction.Referral}
          onClose={() => closeActionModal(ConsultationAction.Referral)}
          specialistName={selectedConsultation?.specialist?.name || ''}
        />
      )}
      {accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && actionModalOpen === ConsultationAction.FollowUp && selectedConsultation && (
        <FollowupModal
          case_id={selectedConsultation.id}
          specialist_name={selectedConsultation?.specialist?.name || ''}
          isOpen={actionModalOpen === ConsultationAction.FollowUp}
          onClose={() => closeActionModal(ConsultationAction.FollowUp)}
        />
      )}

      {accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && (
        <ChatPanel
          open={actionModalOpen === ConsultationAction.Chat && selectedConsultation !== null}
          case_id={selectedConsultation?.id || 0}
          case_status={selectedConsultation?.status.id || ConsultationStatus.AwaitingDoctorReview}
          reciever_profile_photo={selectedConsultation?.specialist_photo || ''}
          onClose={() => closeActionModal(ConsultationAction.Chat)}
          chatPanelRef={chatPanelRef}
          reciever_name={selectedConsultation?.specialist?.name || ''}
          specialization={selectedConsultation?.specialist?.specialization?.label || ''}
        />
      )}

      {/* Duplicate Files Modal */}
      <DuplicateFilesModal
        open={showDuplicateModal}
        onClose={() => {
          setShowDuplicateModal(false);
          setDuplicateFileNames([]);
          setUploadedFilesForDuplicateCheck([]);
          setUploadConsultationId(null);
        }}
        duplicateFileNames={duplicateFileNames}
      />

      {/* Invalid Files Modal */}
      <InvalidFilesModal
        open={showInvalidModal}
        onClose={() => {
          setShowInvalidModal(false);
          setInvalidFileNames([]);
          setRejectedFiles([]);
          dispatch(removeRejectedFiles());
        }}
        invalidFileNames={invalidFileNames}
      />
    </div>
  );
};
